/**
 * Product management — Firestore is the single source of truth.
 * KITCHEN_PRODUCTS_SEED is kept as offline fallback only.
 * All reads prefer Firestore (via REST API). localStorage is a cache.
 */

import { restGet, restSet, restDelete, restList, restBatchSet, waitForAuth } from '../lib/firestoreRest'
import { getDepartmentBranch, migrateProductPrices } from '../lib/productPricing'

// ─── Offline fallback seed — NOT source of truth ───
export const KITCHEN_PRODUCTS_SEED = [
  { code: 'FMK', name: 'Full Mukokoroshi', category: 'mukokoroshi', qps: 4, unitPrice: 85, saleType: 'discrete' },
  { code: 'HMK', name: 'Hafu Ya Mukokoroshi', category: 'mukokoroshi', qps: 2, unitPrice: 65, saleType: 'discrete' },
  { code: 'MKP', name: 'Mukokoroshi Plate', category: 'mukokoroshi', qps: 1, unitPrice: 30, saleType: 'discrete' },
  { code: 'MUK', name: 'Mukokoroshi', category: 'mukokoroshi', qps: null, unitPrice: 18, saleType: 'discrete' },
  { code: 'FS', name: 'Full Steak', category: 'steak', qps: 2.5, unitPrice: 85, saleType: 'discrete', crossPoolAlloc: { steak: 60, vors: 18 } },
  { code: 'HST', name: 'Half Steak', category: 'steak', qps: 1.5, unitPrice: 65, saleType: 'discrete', crossPoolAlloc: { steak: 30, vors: 18 } },
  { code: 'STO', name: 'Steak Only', category: 'steak', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'FSTV', name: 'Full Stew Vors', category: 'stew vors', qps: 3, unitPrice: 85, saleType: 'discrete' },
  { code: 'HSTV', name: 'Half Stew Vors', category: 'stew vors', qps: 2, unitPrice: 65, saleType: 'discrete' },
  { code: 'STV', name: 'Stew Vors', category: 'stew vors', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'VOR', name: 'Vors', category: 'vors', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'KHW', name: 'Khwandadza Khuhu x3', category: 'khwanda', qps: 3, unitPrice: 12, saleType: 'discrete' },
  { code: 'khp', name: 'Khwanda Plate', category: 'khwanda', qps: 3, unitPrice: 30, saleType: 'discrete' },
  { code: 'hkh', name: 'Half Khwanda Dza Khuhu', category: 'khwanda', qps: 5, unitPrice: 40, saleType: 'discrete' },
  { code: 'LIV', name: 'Liver', category: 'chicken liver', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'FLV', name: 'Full Liver', category: 'chicken liver', qps: 35, unitPrice: 80, saleType: 'continuous_fixed' },
  { code: 'HLV', name: 'Half Ya Liver', category: 'chicken liver', qps: 20, unitPrice: 55, saleType: 'continuous_fixed' },
  { code: 'lvp', name: 'Liver Plate', category: 'chicken liver', qps: 15, unitPrice: 30, saleType: 'continuous_fixed' },
  { code: 'MAL', name: 'Mala A Khuhu', category: 'mala', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'FML', name: 'Full Mala', category: 'mala', qps: 40, unitPrice: 75, saleType: 'continuous_fixed' },
  { code: 'HML', name: 'Hafu Ya Mala A Khuhu', category: 'mala', qps: 25, unitPrice: 50, saleType: 'continuous_fixed' },
  { code: 'mlp', name: 'Mala Plate', category: 'mala', qps: 20, unitPrice: 30, saleType: 'continuous_fixed' },
  { code: 'MAM', name: 'Mala A Mukokoroshi', category: 'mala', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'MAS', name: 'Mashonzha', category: 'mashondzha', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'fms', name: 'Full Mashonzha', category: 'mashondzha', qps: 40, unitPrice: 70, saleType: 'continuous_fixed' },
  { code: 'MSP', name: 'Mashonzha Plate', category: 'mashondzha', qps: 20, unitPrice: 30, saleType: 'continuous_fixed' },
  { code: 'MAK', name: 'Makhaha', category: 'makhaha', qps: 10, unitPrice: 10, saleType: 'fixed_unit' },
  { code: 'MUT', name: 'Mutuku', category: 'mutuku', qps: 10, unitPrice: 10, saleType: 'fixed_unit' },
  { code: 'PAP', name: 'Pap Only', category: 'pap only', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'RUS', name: 'Russian', category: 'russian', qps: null, unitPrice: 8, saleType: 'fixed_unit' },
  { code: 'SAL', name: 'Salad', category: 'salad', qps: null, unitPrice: 10, saleType: 'fixed_unit' },
  { code: 'GWA', name: 'Gwanda La Kholomo', category: 'magwanda', qps: null, unitPrice: null, saleType: 'continuous_mirror' },
  { code: 'mts', name: 'Muroho Wa Mutshaini', category: 'muroho', qps: null, unitPrice: 12, saleType: 'fixed_unit' },
]

import { BAKERY_PRODUCTS_SEED } from './bakeryData'
import { YAWEHS_PRODUCTS_SEED } from './yawehsData'
import { TUCKSHOP_PRODUCTS_SEED } from './tuckshopData'

// ─── In-memory cache (populated from Firestore or fallback) ───
const CACHE_KEY = 'desres_products_cache'
const CACHE_TIMESTAMP_KEY = 'desres_products_cache_ts'
const CACHE_TTL_MS = 10 * 60 * 1000 // 10 min — products added on Device A appear on Device B within 10 min of any getProducts() call
const SEED_FLAG_KEY = 'desres_products_seeded_v2'
let _productsCache = null // { kitchen: [...], bakery: [...], yawehs: [...], tuckshop: [...] }
let _cacheLoadPromise = null
let _isOnline = true
// Tracks whether the post-auth seed-version check has already run this
// browser session. The seed bump check is idempotent but we don't want
// every caller of refreshProducts() to re-issue the restGet for
// products/__meta. Reset only on full page reload.
let _seedCheckedForSession = false
// Epoch ms of the last successful fetchFromFirestore. Persisted to
// localStorage so a page reload doesn't immediately re-fetch. Used
// by getProducts() to decide whether to kick off a background refresh.
let _lastFetchedAt = (() => {
  try { return Number(localStorage.getItem(CACHE_TIMESTAMP_KEY)) || 0 }
  catch { return 0 }
})()
// Guard against multiple concurrent background refreshes triggered by
// rapid-fire getProducts() calls on app load.
let _backgroundRefreshInFlight = false

function markFetched() {
  _lastFetchedAt = Date.now()
  try { localStorage.setItem(CACHE_TIMESTAMP_KEY, String(_lastFetchedAt)) } catch {}
}

/** Kick off a non-blocking background refresh if the cache is older
 *  than CACHE_TTL_MS. Returns immediately; the next getProducts()
 *  call after the refresh completes will see the new data. */
function maybeRefreshInBackground() {
  if (_backgroundRefreshInFlight) return
  if (!_productsCache) return // initial load handles this path
  if (Date.now() - _lastFetchedAt <= CACHE_TTL_MS) return
  _backgroundRefreshInFlight = true
  // Optimistically update the timestamp so concurrent getProducts()
  // calls don't re-trigger. If the refresh fails, reset it.
  const savedTs = _lastFetchedAt
  markFetched()
  refreshProducts().catch(err => {
    // Roll the timestamp back so the next call retries
    _lastFetchedAt = savedTs
    try { localStorage.setItem(CACHE_TIMESTAMP_KEY, String(savedTs)) } catch {}
    console.warn('[products] background refresh failed:', err?.message)
  }).finally(() => {
    _backgroundRefreshInFlight = false
  })
}

export function getSeedForDept(department) {
  if (department === 'kitchen') return KITCHEN_PRODUCTS_SEED
  if (department === 'yawehs') return YAWEHS_PRODUCTS_SEED
  if (department === 'tuckshop') return TUCKSHOP_PRODUCTS_SEED
  return BAKERY_PRODUCTS_SEED
}

// Load cache from localStorage (instant, synchronous)
function loadLocalCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

function saveLocalCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  } catch {}
}

// ─── Seed products to Firestore (UPSERT-ONLY) ───
//
// CURRENT_SEED_VERSION is the version of the canonical product seeds
// shipped with this build. seedIfNeeded skips when the persisted
// version is >= the current one.
//
// Upsert contract (Phase 4): seedIfNeeded never overwrites an existing
// product doc. For each seed entry we first list the current products
// collection; any id that already exists in Firestore is SKIPPED so
// admin price/branch edits survive seed-version bumps. Only missing
// codes get written. This flips the historical behaviour where a seed
// bump silently wiped admin overrides.
//
// Emergency re-seed of a specific product is handled by the
// "Reset to Seed Default" button in Admin → Products.
const CURRENT_SEED_VERSION = 4

async function seedIfNeeded() {
  try {
    const meta = await restGet('products/__meta')
    // Only skip if the persisted seedVersion matches the current build.
    // A missing or older version means the seed needs to be (re-)pushed.
    if (meta && Number(meta.seedVersion) >= CURRENT_SEED_VERSION) return

    if (meta && meta.seeded) {
      console.log(`[products] Seed version bump to v${CURRENT_SEED_VERSION} (was v${meta.seedVersion ?? '?'}) — upserting new codes only…`)
    } else {
      console.log(`[products] Seeding products to Firestore (v${CURRENT_SEED_VERSION})…`)
    }

    const allDepts = ['kitchen', 'bakery', 'yawehs', 'tuckshop']
    const seedDocs = []
    for (const dept of allDepts) {
      const seed = getSeedForDept(dept)
      for (const p of seed) {
        const id = `${dept}_${p.code}`.toLowerCase()
        seedDocs.push({ id, ...p, department: dept })
      }
    }

    // One-time migration path: absorb any legacy localStorage custom
    // products by INSERTING them (never overwriting an existing doc).
    try {
      const customRaw = localStorage.getItem('desres_custom_products')
      if (customRaw) {
        const custom = JSON.parse(customRaw)
        for (const p of custom) {
          if (p.deleted) continue
          const dept = p.department || 'kitchen'
          const id = `${dept}_${p.code}`.toLowerCase()
          if (!seedDocs.some(d => d.id === id)) {
            seedDocs.push({ id, ...p, department: dept })
          }
        }
        localStorage.removeItem('desres_custom_products')
        console.log('[products] Queued custom products from legacy localStorage')
      }
    } catch {}

    // Upsert filter: only write docs whose ids are NOT already in
    // Firestore. The existing docs carry admin-edited prices/branches
    // and must not be clobbered.
    let existingIds = new Set()
    try {
      const existing = await restList('products')
      existingIds = new Set(existing.map(d => String(d.id)))
    } catch (err) {
      console.warn('[products] seed upsert: restList failed, skipping (will retry next boot):', err.message)
      return
    }

    const toInsert = seedDocs.filter(d => !existingIds.has(d.id))
    const skipped = seedDocs.length - toInsert.length

    if (toInsert.length > 0) {
      // Upsert is non-destructive — existing cache entries are still
      // correct. We only need to ensure the newly-inserted codes show
      // up, which the next refresh cycle picks up naturally. Clearing
      // the in-memory cache here would race with concurrent
      // updateProduct() calls (the cache-update branch would no-op and
      // lose the write's effect until the next refresh).
      await restBatchSet('products', toInsert)
    }

    // Always bump the meta so subsequent boots skip the whole list call.
    await restSet('products/__meta', {
      seeded: true,
      seedVersion: CURRENT_SEED_VERSION,
      seedAt: new Date().toISOString(),
      lastRun: new Date().toISOString(),
    })

    console.log(`[products] Seed v${CURRENT_SEED_VERSION}: inserted ${toInsert.length} new, preserved ${skipped} existing`)
  } catch (err) {
    console.warn('[products] Seed failed (will use fallback):', err.message)
  }
}

// ─── Fetch all products from Firestore ───
async function fetchFromFirestore() {
  const docs = await restList('products')
  // Filter out __meta document
  const products = docs.filter(d => d.id !== '__meta' && !d.id.startsWith('__'))

  const cache = { kitchen: [], bakery: [], yawehs: [], tuckshop: [] }
  for (const p of products) {
    const dept = p.department || 'kitchen'
    if (cache[dept]) {
      cache[dept].push(p)
    }
  }
  return cache
}

// ─── Initialize: wait for auth → fetch from Firestore → fallback to seed ───
async function initProducts() {
  if (_productsCache) return _productsCache

  // First: load from localStorage cache instantly
  const localCache = loadLocalCache()
  if (localCache) {
    _productsCache = localCache
  }

  // Wait for auth — if not logged in, fallback immediately
  const user = await waitForAuth()
  if (!user) {
    console.log('[products] Not authenticated — using fallback seed')
    if (!_productsCache) _productsCache = buildSeedCache()
    return _productsCache
  }

  // User is authenticated — fetch from Firestore with valid token
  try {
    await seedIfNeeded()
    _seedCheckedForSession = true   // already done; refreshProducts won't repeat it
    const firestoreCache = await fetchFromFirestore()

    const hasData = Object.values(firestoreCache).some(arr => arr.length > 0)
    if (hasData) {
      _productsCache = firestoreCache
      saveLocalCache(firestoreCache)
      markFetched()
      _isOnline = true
    } else if (!_productsCache) {
      _productsCache = buildSeedCache()
    }
  } catch (err) {
    console.warn('[products] Firestore unavailable, using fallback:', err.message)
    _isOnline = false
    if (!_productsCache) {
      _productsCache = buildSeedCache()
    }
  }

  return _productsCache
}

function buildSeedCache() {
  return {
    kitchen: [...KITCHEN_PRODUCTS_SEED],
    bakery: [...BAKERY_PRODUCTS_SEED],
    yawehs: [...YAWEHS_PRODUCTS_SEED],
    tuckshop: [...TUCKSHOP_PRODUCTS_SEED],
  }
}

// Trigger initialization on import (non-blocking)
_cacheLoadPromise = initProducts()

// ─── Public API: getProducts (synchronous — returns cached data) ───
// This MUST remain synchronous for backward compat with 20+ consumer files.
// Returns seed fallback if Firestore hasn't loaded yet.
//
// Stale-while-revalidate: if the cache is older than CACHE_TTL_MS,
// a non-blocking Firestore refresh is kicked off. This call still
// returns the current cache immediately — the new data arrives via
// localStorage + cache update by the next getProducts() invocation.
// This makes products added on Device A visible on Device B within
// ~10 minutes of any product-related UI interaction.
export function getProducts(department = 'kitchen') {
  maybeRefreshInBackground()
  if (_productsCache && _productsCache[department]) {
    return _productsCache[department].filter(p => !p.deleted)
  }
  // Fallback to seed while Firestore loads
  return getSeedForDept(department).filter(p => !p.deleted)
}

// Async version for components that can await
export async function getProductsAsync(department = 'kitchen') {
  await _cacheLoadPromise
  return getProducts(department)
}

// Check if products are from Firestore or fallback
export function isProductsOnline() {
  return _isOnline
}

// ─── Backward compat Proxy ───
export const KITCHEN_PRODUCTS = new Proxy([], {
  get(target, prop) {
    const products = getProducts('kitchen')
    if (prop === Symbol.iterator) return products[Symbol.iterator].bind(products)
    if (prop === 'length') return products.length
    if (typeof prop === 'string' && !isNaN(prop)) return products[Number(prop)]
    if (typeof products[prop] === 'function') return products[prop].bind(products)
    return products[prop]
  }
})

// ─── CRUD: write directly to Firestore, update cache ───

export async function addProduct(product, department = 'kitchen') {
  const id = `${department}_${product.code}`.toLowerCase()
  const record = { ...product, department, createdAt: new Date().toISOString() }
  await restSet(`products/${id}`, record)

  // Update local cache and stamp the fetch timestamp — the cache is
  // now up-to-date for this device, so background refreshes can wait.
  if (_productsCache && _productsCache[department]) {
    const idx = _productsCache[department].findIndex(p => p.code.toLowerCase() === product.code.toLowerCase())
    if (idx >= 0) _productsCache[department][idx] = { id, ...record }
    else _productsCache[department].push({ id, ...record })
    saveLocalCache(_productsCache)
    markFetched()
  }
  return record
}

export async function updateProduct(code, updates, department = 'kitchen') {
  const id = `${department}_${code}`.toLowerCase()
  const existing = getProducts(department).find(p => p.code.toLowerCase() === code.toLowerCase())
  if (!existing) return null

  // Preserve branch-pricing state unless the caller has explicitly supplied
  // new values. Any updateProduct() call that forgets `prices` /
  // `priceHistory` would otherwise wipe an admin's edits through the
  // object spread below.
  const safeUpdates = { ...updates }
  if (!('prices' in updates) && existing.prices) {
    safeUpdates.prices = existing.prices
  }
  if (!('priceHistory' in updates) && existing.priceHistory) {
    safeUpdates.priceHistory = existing.priceHistory
  }

  const updated = { ...existing, ...safeUpdates, department, updatedAt: new Date().toISOString() }
  delete updated.id // don't nest id inside document
  await restSet(`products/${id}`, updated)

  // Update local cache
  if (_productsCache && _productsCache[department]) {
    const idx = _productsCache[department].findIndex(p => p.code.toLowerCase() === code.toLowerCase())
    if (idx >= 0) _productsCache[department][idx] = { id, ...updated }
    else _productsCache[department].push({ id, ...updated })
    saveLocalCache(_productsCache)
    markFetched()
  }
  return updated
}

/** Update the branch-specific price for a single product. Appends an
 *  entry to priceHistory and keeps unitPrice in sync when the edited
 *  branch matches the product's primary branch (derived from department).
 *  Returns { oldPrice, newPrice, historyEntry }. */
export async function updateProductPrice(code, department, branch, newPrice, adminEmail) {
  const product = getProducts(department).find(p => p.code.toLowerCase() === String(code).toLowerCase())
  if (!product) throw new Error(`Product ${code} not found in ${department}`)

  const currentPrices = product.prices || {}
  const oldPrice = branch in currentPrices ? currentPrices[branch] : (product.unitPrice ?? null)

  const normalisedNew = newPrice === null || newPrice === '' ? null : Number(newPrice)
  const historyEntry = {
    branch,
    fromPrice: oldPrice,
    toPrice: normalisedNew,
    changedAt: new Date().toISOString(),
    changedBy: adminEmail || 'unknown',
  }

  const updates = {
    prices: { ...currentPrices, [branch]: normalisedNew },
    priceHistory: [...(product.priceHistory || []), historyEntry],
  }

  // Keep unitPrice in sync only when the edited branch is the product's
  // primary branch (derived from department). Avoids leaking the Pretoria
  // price into a kitchen doc's legacy field.
  const primaryBranch = getDepartmentBranch(department)
  if (branch === primaryBranch && normalisedNew != null) {
    updates.unitPrice = normalisedNew
  }

  await updateProduct(code, updates, department)
  return { oldPrice, newPrice: normalisedNew, historyEntry }
}

/** Reset a single product to its seed default. Rewrites name, category,
 *  qps, unitPrice, saleType from the shipped seed; derives a fresh
 *  `prices` map from the seed's unitPrice and the product's primary
 *  branch. Price history is preserved; a final entry is appended to
 *  record the reset. */
export async function resetProductToSeedDefault(code, department, adminEmail) {
  const seed = getSeedForDept(department).find(
    p => String(p.code).toLowerCase() === String(code).toLowerCase()
  )
  if (!seed) throw new Error(`No seed entry for ${code} in ${department}`)

  const existing = getProducts(department).find(
    p => p.code.toLowerCase() === String(code).toLowerCase()
  )
  const primaryBranch = getDepartmentBranch(department)
  const seedPrice = seed.unitPrice ?? null
  const prices = { limpopo: null, pretoria: null }
  if (primaryBranch) prices[primaryBranch] = seedPrice

  const historyEntry = {
    branch: primaryBranch || 'unknown',
    fromPrice: existing?.prices?.[primaryBranch] ?? existing?.unitPrice ?? null,
    toPrice: seedPrice,
    changedAt: new Date().toISOString(),
    changedBy: adminEmail || 'unknown',
    note: 'reset-to-seed',
  }

  const priceHistory = [...(existing?.priceHistory || []), historyEntry]

  return updateProduct(code, {
    name: seed.name,
    category: seed.category,
    qps: seed.qps ?? null,
    unitPrice: seedPrice,
    saleType: seed.saleType,
    prices,
    priceHistory,
  }, department)
}

/** Bulk price update. Accepts `updates = [{ code, department, branch, newPrice }]`.
 *  Skips rows where the new price matches the current price (within R0.01).
 *  Returns { updated, unchanged, failed: [{ code, error }] }. */
export async function bulkUpdatePrices(updates, adminEmail) {
  const results = { updated: 0, unchanged: 0, failed: [] }
  for (const u of (updates || [])) {
    try {
      const product = getProducts(u.department).find(p => p.code.toLowerCase() === String(u.code).toLowerCase())
      if (!product) {
        results.failed.push({ code: u.code, error: 'not found' })
        continue
      }
      const currentRaw = product.prices && (u.branch in product.prices)
        ? product.prices[u.branch]
        : product.unitPrice
      const current = Number(currentRaw) || 0
      const next = u.newPrice === null || u.newPrice === '' ? null : Number(u.newPrice)
      if (next != null && Math.abs(current - next) < 0.01) {
        results.unchanged++
        continue
      }
      await updateProductPrice(u.code, u.department, u.branch, next, adminEmail)
      results.updated++
    } catch (err) {
      results.failed.push({ code: u.code, error: err?.message || 'update failed' })
    }
  }
  return results
}

/** One-shot migration: for every product without a prices map, compute
 *  { limpopo, pretoria } from department + unitPrice and persist via
 *  restBatchSet. Idempotent — already-migrated products are skipped.
 *  Returns { total, migrated, skipped }. */
export async function migrateAllProductPrices() {
  const depts = ['kitchen', 'bakery', 'yawehs', 'tuckshop']
  const all = []
  for (const d of depts) all.push(...getProducts(d))
  const migratedView = migrateProductPrices(all)

  const docs = []
  for (let i = 0; i < all.length; i++) {
    const orig = all[i]
    const next = migratedView[i]
    if (orig === next) continue // already migrated — helper returns same ref
    const id = `${orig.department}_${orig.code}`.toLowerCase()
    docs.push({
      id,
      ...orig,
      prices: next.prices,
      updatedAt: new Date().toISOString(),
    })
  }

  if (docs.length > 0) {
    await restBatchSet('products', docs)
    // Update local cache in place
    for (const d of docs) {
      const dept = d.department
      if (_productsCache?.[dept]) {
        const idx = _productsCache[dept].findIndex(p => p.code.toLowerCase() === String(d.code).toLowerCase())
        if (idx >= 0) _productsCache[dept][idx] = { ...d }
      }
    }
    saveLocalCache(_productsCache)
    markFetched()
  }

  return { total: all.length, migrated: docs.length, skipped: all.length - docs.length }
}

export async function deleteProduct(code, department = 'kitchen') {
  const id = `${department}_${code}`.toLowerCase()
  await restDelete(`products/${id}`)

  // Update local cache
  if (_productsCache && _productsCache[department]) {
    _productsCache[department] = _productsCache[department].filter(
      p => p.code.toLowerCase() !== code.toLowerCase()
    )
    saveLocalCache(_productsCache)
    markFetched()
  }
  return true
}

// Force refresh from Firestore. Also runs seedIfNeeded the first time
// it's called per session, so a device that loaded the app while
// unauthenticated and then signed in will pick up canonical seed
// updates without needing a manual cache clear.
//
// AuthContext calls this from fetchUserProfile after every successful
// login, which is what makes the version-bump propagate to every device
// regardless of whether the user was logged in at the moment products.js
// first imported.
export async function refreshProducts() {
  try {
    // Run seed check once per session — this is the path that propagates
    // the CURRENT_SEED_VERSION bump to devices whose initial initProducts
    // call hit the unauthenticated branch and short-circuited.
    if (!_seedCheckedForSession) {
      try {
        await seedIfNeeded()
      } catch (err) {
        console.warn('[products] seedIfNeeded failed in refreshProducts:', err.message)
      }
      _seedCheckedForSession = true
    }

    const firestoreCache = await fetchFromFirestore()
    const hasData = Object.values(firestoreCache).some(arr => arr.length > 0)
    if (hasData) {
      _productsCache = firestoreCache
      saveLocalCache(firestoreCache)
      markFetched()
      _isOnline = true
    }
  } catch (err) {
    console.warn('[products] Refresh failed:', err.message)
  }
}

// ─── Lookup helpers (unchanged API) ───

export function findProductByCode(code, department = 'kitchen') {
  const products = getProducts(department)
  return products.find(p => p.code.toLowerCase() === code.toLowerCase())
}

export function findProductByName(name, department = 'kitchen') {
  const products = getProducts(department)
  const lower = name.toLowerCase().trim()
  return products.find(p => p.name.toLowerCase() === lower) ||
    products.find(p => p.name.toLowerCase().includes(lower) || lower.includes(p.name.toLowerCase()))
}

export function fuzzyMatch(input, department = 'kitchen') {
  const products = getProducts(department)
  const lower = input.toLowerCase().trim()

  // 1. Exact code match
  const exact = products.find(p => p.code.toLowerCase() === lower)
  if (exact) return { product: exact, confidence: 100 }

  // 2. Exact name match
  const nameExact = products.find(p => p.name.toLowerCase() === lower)
  if (nameExact) return { product: nameExact, confidence: 100 }

  // 3. POS format: "CODE NAME" — extract leading code token
  const leadingToken = lower.split(/\s+/)[0]
  if (leadingToken) {
    const codeMatch = products.find(p => p.code.toLowerCase() === leadingToken)
    if (codeMatch) return { product: codeMatch, confidence: 98 }
  }

  // 4. Name after stripping code prefix
  const nameAfterCode = lower.replace(/^\S+\s+/, '')
  if (nameAfterCode && nameAfterCode !== lower) {
    const nameMatch = products.find(p => p.name.toLowerCase() === nameAfterCode)
    if (nameMatch) return { product: nameMatch, confidence: 95 }
  }

  const normalized = lower
    .replace(/mashonzha/g, 'mashondzha')
    .replace(/mukokoroshi/g, 'mukokoroshi')

  let best = null
  let bestScore = 0
  for (const p of products) {
    const pLower = p.name.toLowerCase()
    const pCode = p.code.toLowerCase()

    if (pLower.includes(normalized) || normalized.includes(pLower)) {
      const score = Math.min(normalized.length, pLower.length) / Math.max(normalized.length, pLower.length) * 100
      if (score > bestScore) { bestScore = score; best = p }
    }

    const codeRegex = new RegExp(`(^|\\s)${pCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`)
    if (codeRegex.test(normalized)) {
      const score = 90
      if (score > bestScore) { bestScore = score; best = p }
    }
  }

  if (best && bestScore >= 50) return { product: best, confidence: Math.round(bestScore) }
  return null
}

// ─── PAP Sizing Config (Firestore-managed) ───

const PAP_DEFAULTS = { full: 20, half: 12, plate: 10 }
let _papConfig = null

export async function getPapConfig() {
  if (_papConfig) return _papConfig
  try {
    const data = await restGet('config/pap_sizing')
    _papConfig = data && data.full ? data : PAP_DEFAULTS
  } catch {
    _papConfig = PAP_DEFAULTS
  }
  return _papConfig
}

export async function savePapConfig(config) {
  await restSet('config/pap_sizing', { ...config, updatedAt: new Date().toISOString() })
  _papConfig = config
}
